import { useEffect, useReducer, useRef, useState } from 'react'
import { toast } from 'react-toastify'
import useLazyEffect from './useLazyEffect'

/**
 * Used for fetch state reduce
 */
const fetchReducer = (state, action) => {
    switch (action.type) {
      case 'loading':
        return { ...initialState, loading: true }
      case 'fetched':
        return { ...initialState, data: action.payload, loading: false }
      case 'error':
        return { ...initialState, error: action.payload, loading: false }
      default:
        return state
    }
}

// Constants
const initialState = {
    error: undefined,
    data: undefined,
    loading: false
}

/**
 * Simple fetch hook
 * Has a crude/very basic cache
 */
const useFetch = ({ url, options, debounceTimeout = 300, runOnMount = true }) => {
  const cache = useRef({})
  const cancelRequest = useRef(false)

  const [state, dispatch] = useReducer(fetchReducer, initialState);
  const [mounted, setMounted] = useState(false);

  /**
   * Use lazy effect to debounce fetch calls on url/options change
   */
  useLazyEffect(() => {
    if (!url || (!runOnMount && !mounted)) return

    cancelRequest.current = false

    const fetchData = async () => {
      dispatch({ type: 'loading' })

      if (cache.current[url]) {
        dispatch({ type: 'fetched', payload: cache.current[url] })
        return
      }

      try {
        const response = await fetch(url, options);

        if (!response.ok) {
          throw new Error(response.statusText)
        }

        const data = await response.json();
        cache.current[url] = data
        if (cancelRequest.current) return

        dispatch({ type: 'fetched', payload: data })
      } catch (error) {
        toast.error('There was an error while loading data')
        if (cancelRequest.current) return

        dispatch({ type: 'error', payload: error })
      }
    }

    fetchData()

    return () => {
      cancelRequest.current = true
    }
  }, [url, options], debounceTimeout);

  // Mount check
  useEffect(() => {
    setMounted(true);
  }, [])

  return state
}

export default useFetch